import React, { useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Print + Email actions for an issued gift card.
 * Rendered inline under each gift-card refund confirmation and next to
 * every issued voucher code in the order detail.
 */
export default function GiftCardActions({ code }) {
  const [busy, setBusy] = useState(false);
  const doPrint = async () => {
    // Open the tab synchronously (inside the click handler) so pop-up
    // blockers don't kill it. We show a "Loading…" placeholder and then
    // replace it with the fetched HTML once the API returns.
    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print."); return; }
    w.document.open();
    w.document.write("<!doctype html><title>Loading gift card…</title><body style='font-family:sans-serif;padding:20px;color:#666'>Loading gift card…</body>");
    w.document.close();
    setBusy(true);
    try {
      const res = await staffApi.get(`/gift-cards/${code}/print.html`, { responseType: "text" });
      // Inject a <base href> pointing at THIS origin so `/brand/cocoon-butterfly.png`
      // (and any other absolute-path assets) resolve against the frontend
      // instead of about:blank inside the new tab.
      const html = String(res.data).replace(
        "<head>",
        `<head><base href="${window.location.origin}/">`
      );
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      try { w.close(); } catch { /* ignore */ }
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  const doEmail = async () => {
    const to = window.prompt(`Email gift card ${code} to:`);
    if (!to) return;
    setBusy(true);
    try {
      await staffApi.post(`/gift-cards/${code}/email`, { to: to.trim() });
      toast.success(`Emailed to ${to.trim()}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <span className="text-[9px] font-sans text-muted-foreground normal-case tracking-normal">
      <button type="button" onClick={doPrint} disabled={busy} className="underline hover:text-primary disabled:opacity-50" data-testid={`gift-print-${code}`}>Print Gift Card</button>
      <span className="mx-1 opacity-40">·</span>
      <button type="button" onClick={doEmail} disabled={busy} className="underline hover:text-primary disabled:opacity-50" data-testid={`gift-email-${code}`}>Email Gift Card</button>
    </span>
  );
}
